// Kırılım grubu ekle / üye ekle penceresi (spec §6.3, §18 DR6; Modal.dc.html sol panel, 560 px).
// Durum makinesi: `edit` → "Önizle ve kaydet" (dryRun, "Hesaplanıyor…") → `preview` (dönüşecek
// değer + sete eklenecek satır) → "Kaydet" (uygulama, "Kaydediliyor…") → `onSaved` + kapanış. Hata
// her adımda "Kaydedilemedi — hiçbir değer değişmedi" bandı + kod metni (`breakdownErrorText`);
// sunucu tek tx olduğundan hiçbir şey yazılmamıştır. Form kuralları lib/kpi-breakdown-group-form.

import { useEffect, useRef, useState } from "react";
import {
  defaultBreakdownRollup,
  type KpiBreakdownGroupCreateBody,
  type KpiBreakdownGroupCreateResult,
} from "@mahir/shared";
import { ApiError } from "../../api/client";
import { breakdownErrorText, KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import {
  applyDimensionTemplate,
  liveSentence,
  templateReplaceNeedsConfirm,
  type CustomDimensionOption,
  type DimensionChoice,
} from "../../lib/kpi-breakdown-dimension-ui";
import {
  buildGroupBody,
  groupFormGate,
  initialGroupForm,
  setFormCustomSlug,
  setFormDimensionLabel,
  setFormLabel,
  type BreakdownRollup,
  type GroupForm,
  type GroupFormContext,
} from "../../lib/kpi-breakdown-group-form";
import { useKpiBreakdownDimensionsStore } from "../../stores/kpi-breakdown-dimensions-store";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { BreakdownDimensionPicker } from "./BreakdownDimensionPicker";
import { BreakdownGroupMembers, type CatalogMemberOption } from "./BreakdownGroupMembers";
import { KpiBreakdownConvertedBand } from "./KpiBreakdownConvertedBand";
import { KpiModal } from "./KpiModal";
import { btnGhost, btnPrimary, fieldStyle, inputStyle, labelStyle } from "./kpi-form-styles";

const C = KPI_BREAKDOWN_COPY.modal;
const ROLLUPS: readonly BreakdownRollup[] = ["sum", "avg", "none"];
const help = { fontSize: "0.875rem", color: "var(--color-text-3)" } as const;

/**
 * İstek sürerken kapanış (Esc / ✕ / arka plan) yok sayılır — Vazgeç/Geri zaten pasif; aksi hâlde
 * pencere kapanır ama sunucu tx'i sürer ve sonuç kimseye ulaşmaz.
 */
export function guardedClose(pending: string | null, onClose: () => void): () => void {
  return () => {
    if (pending === null) onClose();
  };
}

/** Çift gönderim kilidi: önceki çağrı bitmeden gelen çağrı hiç koşmaz (düğme durumu gecikse de). */
export function createSubmitGuard(): (fn: () => Promise<void>) => Promise<void> {
  let inFlight = false;
  return async (fn) => {
    if (inFlight) return;
    inFlight = true;
    try {
      await fn();
    } finally {
      inFlight = false;
    }
  };
}

/**
 * Kirli üye onayı (§19 B5): "Örnek üyelerle değiştir" şablonu uygular; Vazgeç / Esc / ✕ HİÇBİR
 * şeyi değiştirmez (boyut, grup adı ve üyeler olduğu gibi) — yalnız bekleyen seçim temizlenir.
 */
export function dimensionConfirmHandlers(args: {
  form: GroupForm;
  pending: DimensionChoice | null;
  catalogMembers: readonly CatalogMemberOption[];
  onForm: (form: GroupForm) => void;
  clear: () => void;
}): { onConfirm: () => void; onCancel: () => void } {
  return {
    onConfirm: () => {
      if (args.pending) {
        args.onForm(applyDimensionTemplate(args.form, args.pending, args.catalogMembers));
      }
      args.clear();
    },
    onCancel: args.clear,
  };
}

export type GroupModalStep = "edit" | "preview";
export type GroupModalPending = "preview" | "save" | null;

export interface BreakdownGroupModalViewProps {
  ctx: GroupFormContext;
  form: GroupForm;
  step: GroupModalStep;
  pending: GroupModalPending;
  preview: KpiBreakdownGroupCreateResult | null;
  /** hata kodu metni (bant ayrıntısı) */
  error: string | null;
  clientName: string;
  catalogMembers: readonly CatalogMemberOption[];
  existingMembers: readonly string[];
  onForm: (form: GroupForm) => void;
  onPreview: () => void;
  onSave: () => void;
  onBack: () => void;
  onClose: () => void;
  /** "Yalnız … için oluştur" kutusu (müşteri bağlamı yoksa — katalog — gösterilmez) */
  clientOnlyAvailable?: boolean | undefined;
  /** sözlükteki özel boyutlar ("Sizin tanımladıklarınız", §19 B3) */
  customDimensions?: readonly CustomDimensionOption[] | undefined;
}

const NO_CUSTOM: readonly CustomDimensionOption[] = [];

/** Görünüm (SSR testleri durumu doğrudan verir). */
export function BreakdownGroupModalView(props: BreakdownGroupModalViewProps): JSX.Element {
  const { ctx, form, step, pending, preview, error, onForm } = props;
  const gate = groupFormGate(ctx, form);
  const locked = step === "preview" || pending !== null;
  const shared = !form.clientOnly;
  const isRate = defaultBreakdownRollup(ctx.total.unit) === "none";
  // §19 B5: elle düzenlenmiş üyeler varken boyut değişimi → şablonla değiştir / koru onayı
  const [pendingChoice, setPendingChoice] = useState<DimensionChoice | null>(null);
  const choose = (choice: DimensionChoice): void => {
    const slug = choice.kind === "preset" ? choice.slug : choice.dim.slug;
    if (slug === form.dimension) return; // aynı boyutu yeniden seçmek üyeleri sıfırlamaz
    if (templateReplaceNeedsConfirm(form)) setPendingChoice(choice);
    else onForm(applyDimensionTemplate(form, choice, props.catalogMembers));
  };
  const confirm = dimensionConfirmHandlers({
    form,
    pending: pendingChoice,
    catalogMembers: props.catalogMembers,
    onForm,
    clear: () => setPendingChoice(null),
  });
  // "Diğer": grup adı boyut adından gelir; alan yalnız "Raporda görünen adı değiştir" ile açılır
  const [labelOpen, setLabelOpen] = useState(false);
  const showLabel = form.dimension !== "diger" || labelOpen || form.labelTouched;
  const check = ctx.calcGroupLabel !== null;
  const footer = (
    <>
      <span style={{ ...help, marginRight: "auto" }} aria-live="polite">
        {step === "preview" && preview ? (
          <>
            {C.previewLead}{" "}
            <strong style={{ color: "var(--color-text)" }}>
              {preview.values <= 0
                ? C.previewNoValues
                : props.clientOnlyAvailable === false
                  ? C.previewValuesShared(preview.clients, preview.values)
                  : C.previewValues(preview.values)}
            </strong>
          </>
        ) : gate.ok ? (
          `${C.previewLead} ${C.previewIdle}`
        ) : (
          gate.reason
        )}
      </span>
      {step === "preview" ? (
        <>
          <button type="button" style={btnGhost} disabled={pending !== null} onClick={props.onBack}>
            {C.back}
          </button>
          <button
            type="button"
            style={btnPrimary}
            disabled={pending !== null}
            aria-busy={pending === "save" || undefined}
            onClick={props.onSave}
          >
            {pending === "save" ? C.saving : C.save}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            style={btnGhost}
            disabled={pending !== null}
            onClick={props.onClose}
          >
            {C.cancel}
          </button>
          <button
            type="button"
            data-testid="kpi-breakdown-preview"
            style={{ ...btnPrimary, opacity: gate.ok && pending === null ? 1 : 0.5 }}
            disabled={!gate.ok || pending !== null}
            aria-busy={pending === "preview" || undefined}
            onClick={props.onPreview}
          >
            {pending === "preview" ? C.previewing : C.preview}
          </button>
        </>
      )}
    </>
  );

  return (
    <KpiModal
      open
      kicker={C.kicker}
      title={ctx.group ? C.titleMembers(ctx.group.label) : C.titleNew}
      width={560}
      dirty={props.form.members.some((m) => m.label.trim() !== "") || form.dimension !== ""}
      onClose={guardedClose(pending, props.onClose)}
      footer={footer}
    >
      <div className="grid gap-3" style={{ fontSize: "1rem" }}>
        <div className="flex items-center gap-2" style={help} data-testid="kpi-breakdown-scope">
          <span
            aria-hidden="true"
            style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--color-primary)" }}
          />
          <strong style={{ color: "var(--color-text)" }}>
            {shared ? C.scopeShared : C.scopeClient}
          </strong>
          <span>{shared ? C.scopeSharedRest : C.scopeClientRest(props.clientName)}</span>
        </div>
        {props.clientOnlyAvailable !== false &&
        ctx.totalClientId === null &&
        ctx.group?.clientId == null ? (
          <label className="flex items-center gap-2" style={{ ...help, minHeight: 32 }}>
            <input
              type="checkbox"
              checked={form.clientOnly}
              disabled={locked}
              onChange={(e) => onForm({ ...form, clientOnly: e.target.checked })}
            />
            {C.clientOnlyToggle(props.clientName)}
          </label>
        ) : null}
        {ctx.parentMember ? (
          <div style={help} data-testid="kpi-breakdown-parent-nested">
            <strong style={{ color: "var(--color-text)" }}>
              {C.parentLineNested(
                ctx.parentMember.label,
                ctx.parentMember.groupLabel,
                ctx.total.label,
              )}
            </strong>
            {ctx.total.unit ? ` ${C.parentUnit(ctx.total.unit)}` : ""}
          </div>
        ) : (
          <div style={help}>
            {C.parentLine} <strong style={{ color: "var(--color-text)" }}>{ctx.total.label}</strong>
            {ctx.total.unit ? ` ${C.parentUnit(ctx.total.unit)}` : ""}
          </div>
        )}

        {ctx.group ? (
          <div style={help} data-testid="kpi-breakdown-group-summary">
            <strong style={{ color: "var(--color-text)" }}>{ctx.group.label}</strong>
            {ctx.group.breakdown
              ? ` · ${C.readonlyGroup(ctx.group.breakdown.complete, ctx.group.breakdown.rollup)}`
              : ""}
          </div>
        ) : (
          <>
            <BreakdownDimensionPicker
              form={form}
              rootCode={ctx.rootCode ?? null}
              custom={props.customDimensions ?? NO_CUSTOM}
              used={ctx.usedDimensions}
              disabled={locked}
              onChoose={choose}
              onDimensionLabel={(v) => onForm(setFormDimensionLabel(form, v))}
              onSlug={(v) => onForm(setFormCustomSlug(form, v))}
            />
            {showLabel ? (
              <div style={fieldStyle}>
                <label style={labelStyle} htmlFor="bd-label">
                  {C.groupLabel}
                </label>
                <input
                  id="bd-label"
                  style={inputStyle}
                  value={form.label}
                  maxLength={200}
                  disabled={locked}
                  onChange={(e) => onForm(setFormLabel(form, e.target.value))}
                />
              </div>
            ) : (
              <button
                type="button"
                data-testid="kpi-breakdown-show-label"
                disabled={locked}
                onClick={() => setLabelOpen(true)}
                style={{
                  ...help,
                  justifySelf: "start",
                  background: "transparent",
                  border: 0,
                  padding: 0,
                  minHeight: 32,
                  color: "var(--color-primary)",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {C.showGroupLabel}
              </button>
            )}
            <label className="flex items-start gap-2" style={{ minHeight: 32 }}>
              <input
                type="checkbox"
                checked={form.complete}
                disabled={locked}
                style={{ marginTop: 4, width: 18, height: 18 }}
                onChange={(e) => onForm({ ...form, complete: e.target.checked })}
              />
              <span>{C.complete}</span>
            </label>
            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <legend style={labelStyle}>{C.rollupLegend}</legend>
              {ROLLUPS.map((r) => (
                <label key={r} className="flex items-start gap-2" style={{ padding: "6px 0" }}>
                  <input
                    type="radio"
                    name="bd-rollup"
                    value={r}
                    checked={form.rollup === r}
                    disabled={locked}
                    style={{ marginTop: 4, width: 18, height: 18 }}
                    onChange={() => onForm({ ...form, rollup: r })}
                  />
                  <span>
                    {C.rollup[r]}
                    {r === "avg" ? <span style={help}> {C.avgHint}</span> : null}
                  </span>
                </label>
              ))}
              {ctx.calcGroupLabel ? (
                <div style={help}>{C.existingCalcInfo(ctx.calcGroupLabel)}</div>
              ) : isRate ? (
                <div style={help} data-testid="kpi-breakdown-rate-info">
                  {C.rateInfo}
                </div>
              ) : null}
            </fieldset>
            <div
              role="status"
              data-testid="kpi-breakdown-live"
              className="rounded-lg px-3 py-2"
              style={{
                background: "color-mix(in srgb, var(--color-primary) 6%, var(--color-surface))",
                color: "var(--color-text)",
              }}
            >
              {form.complete && form.rollup !== "none" && !check ? (
                <span style={help}>{C.live.lead} </span>
              ) : null}
              <strong>{liveSentence(form, ctx.total.label, { check })}</strong>
            </div>
          </>
        )}

        <BreakdownGroupMembers
          members={form.members}
          catalog={props.catalogMembers}
          existing={props.existingMembers}
          disabled={locked}
          onChange={(members) => onForm({ ...form, members, membersTouched: true })}
        />

        {step === "preview" && preview && preview.setRows > 0 ? (
          <div
            className="rounded-lg px-3 py-2 text-sm"
            data-testid="kpi-breakdown-preview-counts"
            style={{ border: "1px solid var(--color-border)", color: "var(--color-text)" }}
          >
            {C.previewRows(preview.setRows)}
          </div>
        ) : null}
        {error ? <KpiBreakdownConvertedBand kind="failed" detail={error} /> : null}
        <div data-kpi-modal-confirm>
          <ConfirmDialog
            open={pendingChoice !== null}
            title={C.replaceMembersConfirm.title}
            body={C.replaceMembersConfirm.body}
            confirmLabel={C.replaceMembersConfirm.confirm}
            cancelLabel={C.replaceMembersConfirm.cancel}
            tone="neutral"
            onConfirm={confirm.onConfirm}
            onCancel={confirm.onCancel}
          />
        </div>
      </div>
    </KpiModal>
  );
}

export interface BreakdownGroupModalProps {
  ctx: GroupFormContext;
  /** null = katalog bağlamı (MetricModal "Yeni grup…"): sete satır eklenmez */
  setId: string | null;
  clientId: string | null;
  clientName: string;
  catalogMembers: readonly CatalogMemberOption[];
  existingMembers: readonly string[];
  /** store `addBreakdownGroup` */
  submit: (
    totalMetricId: string,
    body: KpiBreakdownGroupCreateBody,
  ) => Promise<KpiBreakdownGroupCreateResult>;
  onSaved: (result: KpiBreakdownGroupCreateResult, groupLabel: string) => void;
  onClose: () => void;
}

/** Hata → bant ayrıntısı (21 kodluk tablo; bilinmeyen → sunucu metni / genel metin). */
export function groupModalError(err: unknown): string {
  if (err instanceof ApiError) return breakdownErrorText(err.code, err.message);
  return err instanceof Error ? err.message : KPI_BREAKDOWN_COPY.errorFallback;
}

export function BreakdownGroupModal(props: BreakdownGroupModalProps): JSX.Element {
  const { ctx } = props;
  const [form, setForm] = useState(() => initialGroupForm(ctx));
  const [step, setStep] = useState<GroupModalStep>("edit");
  const [pending, setPending] = useState<GroupModalPending>(null);
  const [preview, setPreview] = useState<KpiBreakdownGroupCreateResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const guard = useRef(createSubmitGuard());
  const customDimensions = useKpiBreakdownDimensionsStore((s) => s.custom);
  const loadDimensions = useKpiBreakdownDimensionsStore((s) => s.load);
  const invalidateDimensions = useKpiBreakdownDimensionsStore((s) => s.invalidate);
  const newGroup = ctx.group === null;
  useEffect(() => {
    if (newGroup) void loadDimensions();
  }, [newGroup, loadDimensions]);
  const body = (dryRun: boolean): KpiBreakdownGroupCreateBody =>
    buildGroupBody(ctx, form, { setId: props.setId, clientId: props.clientId }, dryRun);

  async function run(dryRun: boolean): Promise<void> {
    await guard.current(async () => {
      setPending(dryRun ? "preview" : "save");
      setError(null);
      try {
        const r = await props.submit(
          ctx.parentMember?.metricId ?? ctx.total.metricId,
          body(dryRun),
        );
        if (dryRun) {
          setPreview(r);
          setStep("preview");
        } else {
          // grup kaydı özel boyutu sözlüğe yazmış olabilir — sonraki açılış taze listeyi çeker
          invalidateDimensions();
          props.onSaved(r, ctx.group?.label ?? form.label.trim());
        }
      } catch (err) {
        setError(groupModalError(err));
      } finally {
        setPending(null);
      }
    });
  }

  return (
    <BreakdownGroupModalView
      ctx={ctx}
      form={form}
      step={step}
      pending={pending}
      preview={preview}
      error={error}
      clientName={props.clientName}
      clientOnlyAvailable={props.clientId !== null}
      customDimensions={customDimensions}
      catalogMembers={props.catalogMembers}
      existingMembers={props.existingMembers}
      onForm={setForm}
      onPreview={() => void run(true)}
      onSave={() => void run(false)}
      onBack={() => {
        setStep("edit");
        setPreview(null);
      }}
      onClose={props.onClose}
    />
  );
}
